import React, { useMemo, useState } from "react";
import {
	Box, Typography, TextField, Button, Table, TableBody, TableCell,
	TableHead, TableRow, Grid,
} from "@mui/material";
import { FiDownload } from "react-icons/fi";
import { Helmet } from "react-helmet";
import AdminPageHeader, { PageSpinner } from "../../../components/AdminPageHeader";
import { useAdminData } from "../../../services/adminDataStore";

const CARD = {
	backgroundColor: "#FFFFFF",
	borderRadius: "20px",
	border: "1px solid #EEF0F5",
	boxShadow: "0 2px 8px rgba(17,19,26,0.04)",
};

const TABLE_HEAD_CELL = { color: "#8B93A1", borderColor: "#EEF0F5", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };
const TABLE_BODY_CELL = { color: "#1F2430", borderColor: "#EEF0F5", fontSize: 13, whiteSpace: "nowrap" };

const inputSx = {
	"& .MuiOutlinedInput-root": {
		color: "#1F2430",
		backgroundColor: "#F7F8FC",
		fontSize: 13,
		borderRadius: "12px",
	},
	"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" },
};

function money(value) {
	const n = Number(value);
	if (Number.isNaN(n)) return "$0.00";
	return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function SummaryCard({ label, value, accent }) {
	return (
		<Box
			sx={{
				...CARD,
				p: 2.25,
				height: "100%",
			}}
		>
			<Typography fontSize={12} color="#8B93A1" fontWeight={600} letterSpacing="0.02em">
				{label}
			</Typography>
			<Typography fontSize={24} fontWeight={800} color={accent || "#12131A"} mt={0.5} letterSpacing="-0.4px">
				{value}
			</Typography>
		</Box>
	);
}

export default function Payouts() {
	const { earnings, loaded } = useAdminData();
	const payload = earnings || { creators: [], totals: {}, hold_days: 30 };
	const loading = !loaded;
	const [search, setSearch] = useState("");

	const totals = payload.totals || {};
	const filtered = useMemo(() => {
		const list = payload.creators || [];
		if (!search.trim()) return list;
		const q = search.toLowerCase();
		return list.filter((c) => (
			(c.username || "").toLowerCase().includes(q) ||
			(c.name || "").toLowerCase().includes(q) ||
			(c.email || "").toLowerCase().includes(q) ||
			(c.user_id || "").toLowerCase().includes(q)
		));
	}, [payload.creators, search]);

	const handleExportCsv = () => {
		const headers = [
			"User ID", "Username", "Name", "Email", "Split %",
			"Gross", "Platform fee", "Remainder", "Creator owed", "TLK cut",
			"In hold (30d)", "Available to pay", "Paid out", "Invoices",
			"Week gross", "Week owed", "Month gross", "Month owed", "YTD gross", "YTD owed",
			"Bank on file", "Account holder", "Bank", "Routing number", "Account number", "Account type",
		];
		const rows = filtered.map((c) => [
			c.user_id, c.username || "", c.name || "", c.email || "", c.creator_split_percent,
			c.gross_amount, c.platform_fee_total, c.remainder, c.creator_owed, c.platform_cut,
			c.in_hold, c.available_to_pay, c.paid_out, c.invoice_count,
			c.week_gross, c.week_creator_owed, c.month_gross, c.month_creator_owed,
			c.ytd_gross, c.ytd_creator_owed,
			c.bank_on_file ? "yes" : "no",
			c.account_holder_name || "",
			c.bank_name || "",
			c.routing_number || "",
			c.account_number || "",
			c.account_type || "",
		]);
		rows.push([
			"TOTALS", "", "", "", "",
			totals.gross_amount, totals.platform_fee_total, totals.remainder, totals.creator_owed, totals.platform_cut,
			totals.in_hold, totals.available_to_pay, totals.paid_out, totals.invoice_count,
			totals.week_gross, totals.week_creator_owed, totals.month_gross, totals.month_creator_owed,
			totals.ytd_gross, totals.ytd_creator_owed,
		]);
		const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "creator-earnings.csv";
		a.click();
		URL.revokeObjectURL(url);
	};

	const HEAD = { ...TABLE_HEAD_CELL, px: 1.25, py: 1, textAlign: "right" };
	const CELL = { ...TABLE_BODY_CELL, px: 1.25, py: 1, fontSize: 12, textAlign: "right" };
	const LEFT_HEAD = { ...TABLE_HEAD_CELL, px: 1.25, py: 1 };
	const LEFT_CELL = { ...TABLE_BODY_CELL, px: 1.25, py: 1, fontSize: 12 };

	return (
		<Box className="dashboard-main">
			<Helmet>
				<title>Payouts</title>
			</Helmet>
			<Box maxWidth={1600} mx="auto">
				<AdminPageHeader
					title="Payouts"
					subtitle={`Ledger totals — ${payload.hold_days || 30}-day hold before payout. Gross is collected on the platform.`}
					actions={
						<>
						<TextField
							size="small"
							placeholder="Search creator"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							sx={{ ...inputSx, minWidth: 200 }}
						/>
						<Button
							size="small"
							startIcon={<FiDownload />}
							onClick={handleExportCsv}
							sx={{
								px: 2.5,
								py: 1,
								borderRadius: "14px",
								textTransform: "none",
								fontWeight: 600,
								color: "#fff",
								background: "linear-gradient(145deg, #f43f8d, #a855f7)",
								boxShadow: "0 6px 16px rgba(168,85,247,0.28)",
								whiteSpace: "nowrap",
								"&:hover": { boxShadow: "0 10px 22px rgba(168,85,247,0.38)" },
							}}
						>
							Export CSV
						</Button>
						</>
					}
				/>
				{!loaded ? <PageSpinner /> : (
				<>
				<Grid container spacing={2} mb={2}>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard label="Available to pay" value={money(totals.available_to_pay)} accent="#16A34A" />
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard label="In hold" value={money(totals.in_hold)} accent="#D97706" />
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard label="Creator owed" value={money(totals.creator_owed)} />
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<SummaryCard label="Paid out" value={money(totals.paid_out)} />
					</Grid>
				</Grid>

				<Box p={{ xs: 1, sm: 2.5 }} sx={CARD}>
					<Box sx={{ overflowX: "auto" }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={LEFT_HEAD}>Creator</TableCell>
									<TableCell sx={HEAD}>Gross</TableCell>
									<TableCell sx={HEAD}>Platform fee</TableCell>
									<TableCell sx={HEAD}>Remainder</TableCell>
									<TableCell sx={HEAD}>Creator owed</TableCell>
									<TableCell sx={HEAD}>TLK cut</TableCell>
									<TableCell sx={HEAD}>In hold</TableCell>
									<TableCell sx={HEAD}>Available</TableCell>
									<TableCell sx={HEAD}>Paid out</TableCell>
									<TableCell sx={HEAD}>YTD owed</TableCell>
									<TableCell sx={HEAD}>YTD gross</TableCell>
									<TableCell sx={LEFT_HEAD}>Holder</TableCell>
									<TableCell sx={LEFT_HEAD}>Bank</TableCell>
									<TableCell sx={LEFT_HEAD}>Routing</TableCell>
									<TableCell sx={LEFT_HEAD}>Account</TableCell>
									<TableCell sx={LEFT_HEAD}>Type</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={16} sx={{ ...LEFT_CELL, color: "#8B93A1", py: 4, textAlign: "center" }}>
											Loading earnings…
										</TableCell>
									</TableRow>
								) : (
									<>
										{filtered.map((c) => (
											<TableRow key={c.user_id} hover>
												<TableCell sx={LEFT_CELL}>
													<div style={{ fontWeight: 600 }}>{c.username || c.name || c.user_id}</div>
													{(c.email || c.name) && (
														<div style={{ color: "#8B93A1", fontSize: 11 }}>
															{[c.name, c.email].filter(Boolean).join(" · ")}
														</div>
													)}
												</TableCell>
												<TableCell sx={CELL}>{money(c.gross_amount)}</TableCell>
												<TableCell sx={CELL}>{money(c.platform_fee_total)}</TableCell>
												<TableCell sx={CELL}>{money(c.remainder)}</TableCell>
												<TableCell sx={CELL}>{money(c.creator_owed)}</TableCell>
												<TableCell sx={CELL}>{money(c.platform_cut)}</TableCell>
												<TableCell sx={CELL}>{money(c.in_hold)}</TableCell>
												<TableCell sx={{ ...CELL, color: "#16A34A", fontWeight: 600 }}>{money(c.available_to_pay)}</TableCell>
												<TableCell sx={CELL}>{money(c.paid_out)}</TableCell>
												<TableCell sx={CELL}>{money(c.ytd_creator_owed)}</TableCell>
												<TableCell sx={CELL}>{money(c.ytd_gross)}</TableCell>
												{c.account_number ? (
													<>
														<TableCell sx={LEFT_CELL}>{c.account_holder_name}</TableCell>
														<TableCell sx={LEFT_CELL}>{c.bank_name}</TableCell>
														<TableCell sx={{ ...LEFT_CELL, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
															{c.routing_number}
														</TableCell>
														<TableCell sx={{ ...LEFT_CELL, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
															{c.account_number}
														</TableCell>
														<TableCell sx={LEFT_CELL}>{c.account_type}</TableCell>
													</>
												) : (
													<TableCell sx={{ ...LEFT_CELL, color: "#8B93A1" }} colSpan={5}>Missing</TableCell>
												)}
											</TableRow>
										))}
										{!loading && filtered.length === 0 && (
											<TableRow>
												<TableCell colSpan={16} sx={{ ...LEFT_CELL, color: "#8B93A1", py: 4, textAlign: "center" }}>
													No creators found.
												</TableCell>
											</TableRow>
										)}
										{filtered.length > 0 && (
											<TableRow>
												<TableCell sx={{ ...LEFT_CELL, fontWeight: 700 }}>Totals</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.gross_amount)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.platform_fee_total)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.remainder)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.creator_owed)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.platform_cut)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.in_hold)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700, color: "#16A34A" }}>{money(totals.available_to_pay)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.paid_out)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.ytd_creator_owed)}</TableCell>
												<TableCell sx={{ ...CELL, fontWeight: 700 }}>{money(totals.ytd_gross)}</TableCell>
												<TableCell sx={LEFT_CELL} colSpan={5} />
											</TableRow>
										)}
									</>
								)}
							</TableBody>
						</Table>
					</Box>
				</Box>
				</>
				)}
			</Box>
		</Box>
	);
}
